import React from 'react';
import { Box, Typography, Link, Divider } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircle from '@mui/icons-material/AccountCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuIcon from '@mui/icons-material/Menu';

export default function GettingStarted() {
  return (
    <Box
      sx={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px',
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
      }}
    >
      <Typography variant="h4" gutterBottom>
        Getting Started
      </Typography>
      <Typography variant="body2" gutterBottom sx={{ fontStyle: 'italic' }}>
        Last Updated: 13/02/2026
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />
      <Typography variant="body1" paragraph>
        MicGro Manager is a web-based app designed to help small microgreen growers streamline certain aspects of
        their business—from planning and tracking plantings and harvests to managing customer orders, invoicing, and daily
        care tasks. By centralizing your seed varieties, tray inventory, crop schedules, and customer data in one place,
        the app automatically generates reminders for key actions like soaking, sowing, watering, harvesting and delivery,
        and even creates PDF invoices for completed orders. With customizable settings (units, date formats, light/dark
        mode), and a mobile-responsive interface, MicGro Manager makes it simple to stay organized, reduce manual errors,
        and spend more time growing your microgreens instead of chasing spreadsheets.
      </Typography>

      <Typography variant="h6" gutterBottom>
        Prerequisites
      </Typography>
      <Typography component="div" paragraph>
        <ul>
          <li>A modern web browser (Firefox, Chrome, Edge, Safari, Opera)</li>
          <li>An authorised account (automatic for Google sign in, verification email for email &amp; password signup)</li>
          <li>Subscription time (provided if you are part of the Beta)</li>
          <li>Any hardware/software notes (e.g. mobile-friendly, desktop only, etc.)</li>
        </ul>
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />
      <Typography variant="h6" gutterBottom>
        1. Sign Up &amp; Sign In
      </Typography>
      <Typography variant="subtitle1" gutterBottom>
        1.1 Create an Account
      </Typography>
      <Typography variant="body1" gutterBottom sx={{ py: 1, px: 4 }}>
        When you arrive at the site, you will see two buttons at the top left of the screen. <strong>Sign Up</strong> and <strong>Login</strong>.
      </Typography>
      <Typography component="div" paragraph>
        <ul>
          <li>
            Click <strong>Sign Up</strong> in the top-right corner to make an account or click <strong>Login</strong> to
            login with an existing account. Both windows will also allow you to sign up/login with a Google account.
          </li>
          <li>
            Provide your email, password and chosen username and click <strong>Sign Up</strong>.
          </li>
          <li>
            You’ll be presented with a screen confirming your verification email has been sent. If it does not arrive,
            please click the button to resend. At this point you only have access to the help pages and logout button.
          </li>
          <li>
            Once you click the link in the email your account will be verified. This step is not required for Google
            sign-ups.
          </li>
          <li>
            You will not be able to access the app until you have subscribed. For the purposes of the Beta, you will be
            provided with a Timecode, which can be redeemed on the Account page (found in the user menu <AccountCircle fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />at the top
            right when logged in).
          </li>
        </ul>
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />
      <Typography variant="h6" gutterBottom>
        2. Navigating the App
      </Typography>
      <Typography component="div" paragraph>
        <ul>
          <li><strong>Navigation Menu (top-bar on large screens, in <MenuIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} /> menu on mobile):</strong></li>
          <ul>
            <li><strong>Dashboard</strong> – overview of harvests, orders, tasks.</li>
            <li><strong>Harvests</strong> – record new plantings &amp; yields.</li>
            <li><strong>Orders</strong> – create customer orders &amp; generate invoices.</li>
            <li><strong>Tasks</strong> – view upcoming tasks in table or calendar and add custom tasks.</li>
            <li><strong>Management</strong> – customer database, inventory, and farm configuration.</li>
            <ul>
              <li><strong>Seeds</strong> – add or edit crop varieties.</li>
              <li><strong>Trays</strong> – manage your trays &amp; their status.</li>
              <li><strong>Farm</strong> – members/roles, invite code, farm settings, backup tools, and farm danger actions.</li>
            </ul>
          </ul>
        </ul>
      </Typography>
      <Typography variant="body1" paragraph>
        If you are a member of more than one farm, use the farm tile in the account drawer (right-side user menu) to switch active farm.
        The side button shows <strong>+</strong> when you have one farm, and a dropdown chevron when you have multiple farms.
      </Typography>
      <Typography variant="body1" gutterBottom sx={{ py: 1, px: 4 }}>
        When you see this symbol: <HelpOutlineIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />, mouse over to display a tooltip with further information about that feature.
      </Typography>
      <Typography variant="h6" gutterBottom>
        2.5 Viewing Record Details
      </Typography>
      <Typography variant="body1" paragraph>
        Any time you click on a record (customer, seed, tray, harvest, order), a modal window will open displaying all
        the key details for that item. This gives you a quick, readable summary without needing to edit the record directly. 
        This is applicable to harvests and orders both on thier individual pages and the dashboard.
      </Typography>
      <Typography variant="body1" paragraph>
        These popups are designed to help you get a fast overview or verify the right selection when working with complex
        harvests or customer orders. You can close the modal with the close button or click outside the window to dismiss it.
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />
      <Typography variant="h6" gutterBottom>
        3. Managing Seeds
      </Typography>
      <Typography component="div" paragraph>
        <ol>
          <li>Go to Management → Inventory.</li>
          <li>Click <strong>Add Seed</strong> or <strong>Add Tray</strong>.</li>
          <li>Fill in the form (fields with <strong>*</strong> are required).</li>
          <li>Save by pressing the <strong>Add</strong> button.</li>
          <li>Edit, clone or delete seeds or trays anytime using the <SettingsIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />menu.</li>
        </ol>
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />
      <Typography variant="h6" gutterBottom>
        4. Managing Customers
      </Typography>
      <Typography component="div" paragraph>
        <ol>
          <li>Go to Management → Customers.</li>
          <li>Click <strong>Add Customer</strong>.</li>
          <li>Fill in the form (fields with * are required).</li>
          <li>Save by pressing the <strong>Add</strong> button.</li>
          <li>Edit or delete customers anytime using the <SettingsIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />menu.</li>
        </ol>
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />
      <Typography variant="h6" gutterBottom>
        5. Recording Harvests
      </Typography>
      <Typography component="div" paragraph>
        <ol>
          <li>Head to the Harvests page.</li>
          <li>Choose <strong>Add Harvest</strong>.</li>
          <li>Fill in the form (fields with * are required):
            <ul>
              <li>Select seeds and trays you’ve already added on the Inventory page.</li>
              <li>Press <strong>Save Template</strong> to save the currently entered details for later use.</li>
              <li>Templates will appear at the top of the form; press one to load details (minus the start date).</li>
              <li>Estimated yields should be entered on a <strong>PER TRAY</strong> basis</li>
            </ul>
          </li>
          <li>Save by pressing the <strong>Add</strong> button.</li>
          <li>Edit, clone or delete harvests anytime using the <SettingsIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />menu.</li>
        </ol>
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />
      <Typography variant="h6" gutterBottom>
        6. Fulfilling Orders
      </Typography>
      <Typography component="div" paragraph>
        <ol>
          <li>On the Orders page, click <strong>Add Order</strong>.</li>
          <li>Select a customer as added in the Customers page.</li>
          <li>Pick one or more pre-entered harvests and set quantity &amp; price.</li>
          <li>You can create blends by adding multiple lines under a single product entry.</li>
          <li>You can also mark orders as recurring using the recurring controls in the order form.</li>
          <li>Review &amp; Confirm Order.</li>
          <li>The system:
            <ul>
              <li>Generates a PDF invoice</li>
              <li>Stores it under your Invoices</li>
              <li>Removes the order from active list</li>
            </ul>
          </li>
        </ol>
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />
      <Typography variant="h6" gutterBottom>
        7. Tasks &amp; Task Calendar
      </Typography>
      <Typography component="div" paragraph>
        <ol>
          <li>Use toggle at top of Tasks page to view tasks as a table or calendar.</li>
          <li>System-generated tasks include:
            <ul>
              <li>Seed soaking reminders</li>
              <li>Planting dates</li>
              <li>Watering schedule</li>
              <li>Harvest reminders</li>
              <li>Delivery deadlines</li>
            </ul>
          </li>
          <li>To add manually, click <strong>Add Task</strong> and fill out the form:
            <ul>
              <li>Custom task type allows you to pick an emoji and colour.</li>
              <li>Recurring checkbox lets you schedule multiple tasks at your chosen interval.</li>
            </ul>
            Save by pressing the <strong>Add</strong> button.
          </li>
          <li>Mark tasks <strong>Complete</strong> (list view) or click on tasks/task groups (calendar view) to clear them.</li>
        </ol>
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />
      <Typography variant="h6" gutterBottom>
        8. Account &amp; Settings
      </Typography>
      <Typography component="div" paragraph>
        <ol>
          <li>Click the user menu <AccountCircle fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />at the top right and select the <strong>Account</strong> page link.</li>
          <li>Update:
            <ul>
              <li>Display name</li>
              <li>Account info and subscription/timecode details</li>
            </ul>
          </li>
          <li>Farm-specific settings (farm name/address/logo, units, date format, and members) are managed in <strong>Management → Farm</strong>.</li>
          <li>Role permissions:
            <ul>
              <li><strong>Owner:</strong> full control including danger actions and backup/import.</li>
              <li><strong>Admin:</strong> member management and most farm operations.</li>
              <li><strong>Worker/Member:</strong> operational or read-focused access based on role.</li>
            </ul>
          </li>
          <li>Delete Data:
            <ul>
              <li>Account deletion removes your account and any farm data for farms you own (irreversible).</li>
            </ul>
          </li>
        </ol>
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />
      <Typography variant="h6" gutterBottom>
        9. Dark/Light Mode
      </Typography>
      <Typography component="div" paragraph>
        <ul>
          <li>Toggle the switch in the user menu <AccountCircle fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />to switch themes instantly.</li>
        </ul>
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />
      <Typography variant="h6" gutterBottom>
        10. Troubleshooting &amp; Support
      </Typography>
      <Typography component="div" paragraph>
        <ul>
          <li>
            <strong>“Why can’t I add a seed/customer/etc?”:</strong> Check your subscription time—add buttons are greyed out if you have no
            subscription. You may still view and remove existing data.
          </li>
          <li>
            <strong>“Why can’t I see invite code, backup, or farm danger actions?”:</strong> These are restricted by role. Backup/import and farm-level danger actions are owner-only.
          </li>
          <li>
            <strong>“I can’t see the right farm data”:</strong> Confirm your active farm in the account drawer farm selector.
          </li>
          <li>
            <strong>“Help! I deleted all my data!”:</strong> That’s why it’s called “Danger Zone”—you’re responsible for confirming those
            deletions.
          </li>
          <li>
            <strong>Contact Us:</strong> <Link href="mailto:support@hy-gro.co.uk">support@hy-gro.co.uk</Link> or join our Discord. No pressure!
          </li>
        </ul>
      </Typography>
      <Divider sx={{ width: '100%', my: 2 }} />
      <Typography variant="h6" gutterBottom>
        Tips &amp; Best Practices
      </Typography>
      <Typography component="div" paragraph>
        <ul>
          <li><strong>Prepare:</strong> Set up your seeds and trays before beginning plantings.</li>
          <li><strong>Plan ahead:</strong> Use templates for bulk plantings &amp; orders.</li>
          <li><strong>Clean up:</strong> Delete old tasks to keep your calendar tidy.</li>
        </ul>
      </Typography>
    </Box>
  );
}
